import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const emptyDraft = () => ({ custom: '', answers: {}, stepIndex: 0 });
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

function readDraft(key, signature, count) {
  if (!key) return emptyDraft();
  try {
    const stored = JSON.parse(globalThis.sessionStorage?.getItem(key) || 'null');
    const value = stored?.value;
    if (stored?.signature !== signature || Date.now() - stored.savedAt > MAX_AGE_MS
      || !Number.isFinite(stored.savedAt) || typeof value?.custom !== 'string'
      || !value.answers || Array.isArray(value.answers) || typeof value.answers !== 'object'
      || Object.values(value.answers).some(answer => typeof answer !== 'string')
      || !Number.isInteger(value.stepIndex) || value.stepIndex < 0 || value.stepIndex >= count) return emptyDraft();
    return value;
  } catch {
    return emptyDraft();
  }
}

// Tab-scoped storage restores browser refreshes without sharing unfinished
// answers between accounts, tasks, or a replacement question. No auto-submit.
export default function useWorkQuestionDraft(userId, taskId, question) {
  const key = userId && taskId && question?.id
    ? `work-question-draft:${JSON.stringify([userId, taskId, question.id])}` : '';
  const signature = JSON.stringify([question?.question, question?.questions, question?.options, question?.kind, question?.allowCustom]);
  const identity = `${key}:${signature}`;
  const count = Math.max(1, question?.questions?.filter(q => q?.question).length || 0);
  const initial = useMemo(() => readDraft(key, signature, count), [key, signature, count]);
  const [state, setState] = useState(() => ({ identity, value: initial }));
  const cleared = useRef(null);
  const value = state.identity === identity ? state.value : initial;

  useEffect(() => {
    if (!key || cleared.current === identity) return;
    try {
      if (!value.custom && !Object.keys(value.answers).length && value.stepIndex === 0) {
        globalThis.sessionStorage?.removeItem(key);
      } else {
        globalThis.sessionStorage?.setItem(key, JSON.stringify({ signature, savedAt: Date.now(), value }));
      }
    } catch { /* Storage restrictions must not prevent answering. */ }
  }, [identity, key, signature, value]);

  const setField = useCallback((field, update) => {
    setState(previous => {
      const draft = previous.identity === identity ? previous.value : initial;
      return { identity, value: { ...draft, [field]: typeof update === 'function' ? update(draft[field]) : update } };
    });
  }, [identity, initial]);
  const setCustom = useCallback(update => setField('custom', update), [setField]);
  const setAnswers = useCallback(update => setField('answers', update), [setField]);
  const setStepIndex = useCallback(update => setField('stepIndex', update), [setField]);
  const clearDraft = useCallback(() => {
    cleared.current = identity;
    try { if (key) globalThis.sessionStorage?.removeItem(key); } catch { /* Best effort. */ }
  }, [identity, key]);
  return { ...value, setCustom, setAnswers, setStepIndex, clearDraft };
}
